import type { ACTIONS } from '@wix/component-protocol/schema';
import { DISABLED_ANIMATION_NAME } from './components/MenuContent/MenuItem/useAnimationState';

export const MenuOrientation = {
  Horizontal: 'horizontal',
  Vertical: 'vertical',
} as const;
export type MenuOrientationType =
  (typeof MenuOrientation)[keyof typeof MenuOrientation];

export const VerticalDropdownDisplay = {
  AlwaysOpen: 'alwaysOpen',
  ExpandCollapse: 'expandCollapse',
} as const;
export type MenuVerticalDropdownDisplayType =
  (typeof VerticalDropdownDisplay)[keyof typeof VerticalDropdownDisplay];

export const DropdownAnchor = {
  menuItem: 'menuItem',
  menuStretched: 'menuStretched',
  menuCustomWidth: 'menuCustomWidth',
  screen: 'screen',
} as const;
export type DropdownAnchorType =
  (typeof DropdownAnchor)[keyof typeof DropdownAnchor];

export const MenuAnimationName = {
  None: DISABLED_ANIMATION_NAME,
  Calm: 'calm',
  Underline: 'underline',
  Wash: 'wash',
  Bullet: 'bullet',
  Wave: 'wave',
  Bounce: 'bounce',
  Tint: 'tint',
  TextWash: 'textWash',
  Wobble: 'wobble',
  Brackets: 'brackets',
  Float: 'float',
  Shape: 'shape',
  Expand: 'expand',
  Circle: 'circle',
  Shadow: 'shadow',
  LineRise: 'lineRise',
  Overline: 'overline',
  CenterLine: 'centerLine',
  Skew: 'skew',
  Point: 'point',
  Blur: 'blur',
} as const;
export type MenuAnimationNameType =
  (typeof MenuAnimationName)[keyof typeof MenuAnimationName];

export const DropdownAnimationName = {
  None: DISABLED_ANIMATION_NAME,
  RevealFromTop: 'revealFromTop',
  FadeIn: 'fadeIn',
} as const;
export type DropdownAnimationNameType =
  (typeof DropdownAnimationName)[keyof typeof DropdownAnimationName];

export const HamburgerMenuAnimationName = {
  None: DISABLED_ANIMATION_NAME,
  RevealFromRight: 'revealFromRight',
  FadeIn: 'fadeIn',
} as const;
export type HamburgerMenuAnimationNameType =
  (typeof HamburgerMenuAnimationName)[keyof typeof HamburgerMenuAnimationName];

export const DisplayNames = {
  root: {
    elementDisplayName: 'Menu',
    actions: {
      manageMenu: 'Manage Menu',
      design: 'Design',
      settings: 'Settings',
    },
    customActions: {
      format: {
        displayName: 'Change Format',
        panel: {
          menu: 'Menu',
          orientation: 'Orientation',
          overflowItems: {
            displayName: 'Item behavior',
            infoContent:
              'Choose whether visitors scroll to view menu items or see them all displayed at once',
          },
          menuTypeOptions: {
            navbar: 'Menu bar',
            hamburger: 'Hamburger menu',
          },
          orientationOptions: {
            horizontal: 'Horizontal',
            vertical: 'Vertical',
          },
          overflowOptions: {
            scroll: 'Fit to scroll',
            expandMenu: 'Fit to text',
          },
        },
      },
      animatedIcon: {
        displayName: 'Customize Icon',
      },
      hamburgerAnimatedIcon: {
        displayName: 'Customize Menu Icon',
      },
    },
    presets: {
      horizontalScrollNavbar: 'Horizontal Scroll Navigation Bar',
      horizontalHugNavbar: 'Horizontal Hug Navigation Bar',
      verticalNavbar: 'Vertical Navigation Bar',
      hamburgerMenu: 'Hamburger menu',
    },
    states: {
      hamburgerDialog: 'Hamburger menu',
      dropdown: 'Dropdown',
    },
  },
  navbar: {
    elementDisplayName: 'Menu bar',
    displayGroups: {
      dividerBorderGroup: 'Dividers',
    },
    cssCustomProperties: {
      'vertical-dropdown-display': 'Items display',
      'items-width': 'Item width',
      'container-align': 'Item alignment',
      'menu-items-row-gap': 'Space between menu items',
      'menu-items-column-gap': 'Space between menu items',
    },
    actions: {
      manageMenu: 'Manage Menu',
    },
    options: {
      alwaysOpen: 'Open',
      expandCollapse: 'Closed',
      fitToText: 'Fit to text',
      fillMenu: 'Fill menu',
      start: 'Start',
      end: 'End',
      top: 'Top',
      bottom: 'Bottom',
    },
  },
  item: {
    elementDisplayName: 'Menu items',
    cssProperties: {
      justifyContent: 'Text alignment',
      background: 'Background',
      columnGap: 'Space between items and chevrons',
    },
    cssCustomProperties: {
      'item-icon-size': 'Size',
      'item-icon-gap': 'Space between items and icons',
    },
    displayGroups: {
      iconGroup: 'Icons',
    },
    states: {
      hover: 'Hover',
      selected: 'Selected',
    },
  },
  itemLabel: {
    elementDisplayName: "Item's text",
  },
  itemIcon: {
    elementDisplayName: "Item's icon",
  },
  dropdown: {
    elementDisplayName: 'Dropdowns',
    cssCustomProperties: {
      anchor: 'Dropdown width',
      align: 'Dropdown position',
      'items-width': 'Item width',
      'columns-number': 'Number of columns',
      'space-above': 'Space above dropdowns',
      'horizontal-margin': 'Dropdown margin',
      'item-position': 'Dropdown box position',
    },
    options: {
      menuItem: 'Fit to items',
      menuStretched: 'Fit to menu',
      screen: 'Stretch to page',
      start: 'Start',
      center: 'Middle',
      end: 'End',
      fitToText: 'Fit to text',
      fillDropdown: 'Fill dropdowns',
      afterItems: 'After items',
      beforeItems: 'Before items',
      aboveItems: 'Above items',
      belowItems: 'Below items',
    },
    displayGroups: {
      gapGroup: 'Space between items',
      cornersGroup: 'Corner radius',
    },
  },
  dropdownItem: {
    elementDisplayName: 'Dropdown items',
  },
  dropdownSubItem: {
    elementDisplayName: 'Dropdown sub-items',
  },
  scrollButton: {
    elementDisplayName: 'Scroll button',
    cssProperties: {
      background: 'Background',
    },
    cssCustomProperties: {
      'scroll-button-icon-size': 'Size',
    },
    displayGroups: {
      scrollButtonGroup: 'Icon',
    },
  },
  hamburgerMenu: {
    elementDisplayName: 'Hamburger menu',
    displayGroups: {
      cornersGroup: 'Corner radius',
    },
    cssCustomProperties: {
      'hamburger-wrapper-width': 'Width',
      'wrapper-position': 'Position',
      'menu-position': 'Menu Position',
      'close-button-position': 'Close button position',
      'overlay-background': 'Overlay Background',
    },
    options: {
      third: 'Third of page',
      twoThirds: 'Two thirds of page',
      half: 'Half page',
      full: 'Full page',
      start: 'Start',
      middle: 'Middle',
      end: 'End',
      top: 'Top',
      bottom: 'Bottom',
    },
  },
  hamburgerOpenButton: {
    elementDisplayName: 'Hamburger button',
  },
  hamburgerCloseButton: {
    elementDisplayName: 'Close Button',
  },
};

// Menu already relies on the built-in "elements" action in preset display
// filters, but the current component-protocol typings do not expose it.
export const menuBuiltInActions = {
  elements: 'elements' as typeof ACTIONS.ACTION_NAME.design,
} as const;

export const MENU_ANIMATED_ICON_SPEC = 'sp.erc.menuAnimatedIcon' as const;

export const customActionKeys = {
  animatedIcon: 'animatedIcon',
  hamburgerAnimatedIcon: 'hamburgerAnimatedIcon',
} as const;

export const selectors = {
  menu: 'menu',
  navbar: 'navbar',
  item: 'item',
  dropdown: 'dropdown',
  dropdownItem: 'dropdown-item',
  dropdownSubItem: 'dropdown-sub-item',
  scrollButton: 'scroll-button',
  animatedIcon: 'animated-icon',
  itemLabel: 'item-label',
  hamburgerMenu: 'hamburger-menu',
  hamburgerMenuContent: 'hamburger-menu-content',
  hamburgerMenuHeaderContent: 'hamburger-menu-header-content',
  hamburgerMenuOpenButton: 'hamburger-open-button',
  hamburgerMenuCloseButton: 'hamburger-close-button',
};

export const elementKeys = {
  navbar: 'navbar',
  hamburgerMenu: 'hamburgerMenu',
  hamburgerOpenButton: 'hamburgerOpenButton',
  hamburgerCloseButton: 'hamburgerCloseButton',
  item: 'item',
  subItem: 'subItem',
  scrollButton: 'scrollButton',
  dropdown: 'dropdown',
  animatedIcon: 'animatedIcon',
  itemLabel: 'itemLabel',
};

export const presets = {
  horizontalScrollNavbar: 'horizontalScrollNavbar',
  horizontalHugNavbar: 'horizontalHugNavbar',
  verticalNavbar: 'verticalNavbar',
  hamburgerMenu: 'hamburgerMenu',
};

export const states = {
  dropdown: 'dropdown',
  hamburgerDialog: 'hamburgerDialog',
  hamburgerDialogDropdown: 'hamburgerDialogDropdown',
};

export const DesignStates = {
  item: {
    hover: {
      displayName: DisplayNames.item.states.hover,
      className: 'menu__item--hover',
    },
    selected: {
      displayName: DisplayNames.item.states.selected,
      className: 'menu__item--selected',
    },
  },
  dropdownItem: {
    hover: {
      displayName: 'Hover',
      className: 'menu__dropdown-item--hover',
    },
    selected: {
      displayName: 'Selected',
      className: 'menu__dropdown-item--selected',
    },
  },
  dropdownSubItem: {
    hover: {
      displayName: 'Hover',
      className: 'menu__dropdown-sub-item--hover',
    },
    selected: {
      displayName: 'Selected',
      className: 'menu__dropdown-sub-item--selected',
    },
  },
  scrollButton: {
    hover: {
      displayName: 'Hover',
      className: 'menu__scroll-button--hover',
    },
  },
} as const;

export const DropdownAppliedStyleProperties = {
  anchor: '--wix-menu-dropdown-anchor',
};

export const DropdownAnchorPositions = {
  menuItem: '0',
  menuStretched: '1',
  screen: '2',
} as const;

export const ARIA_LABEL_NAMESPACE = 'ariaLabels';

export const translationsKeys = {
  menuNavAriaLabel: 'dropDownMenu_AriaLabel_TopLevel_SiteNavigation',
  dropdownButtonAriaLabel: 'menu_dropdown_chevron_button_aria_label',
  hamburgerOpenButtonAriaLabel: 'menu_hamburger_open_button_aria_label',
  hamburgerCloseButtonAriaLabel: 'menu_hamburger_close_button_aria_label',
};

export type Translations = {
  translations: {
    dropdownButtonAriaLabel?: string;
    menuNavAriaLabel?: string;
    hamburgerOpenButtonAriaLabel?: string;
    hamburgerCloseButtonAriaLabel?: string;
  };
};

export const defaultTranslations = {
  menuNavAriaLabel: 'Site',
  dropdownButtonAriaLabel: 'More pages',
  hamburgerOpenButtonAriaLabel: 'Menu',
  hamburgerCloseButtonAriaLabel: 'Close Menu',
} satisfies Translations['translations'];

export const MenuParts = {
  Root: 'menu-root',
  Navbar: 'navbar',
  DropdownContainer: 'dropdown-container',
  DropdownButton: 'dropdown-button',
  DropdownIcon: 'dropdown-icon',
  Label: 'label',
  DropdownItem: 'dropdown-item',
  DropdownItemLabel: 'dropdown-item-label',
  DropdownSubItem: 'dropdown-subitem',
  DropdownSubItemLabel: 'dropdown-subitem-label',
  MenuItem: 'menu-item',
  MenuItemContent: 'menu-item-content',
  MenuItemLink: 'menu-item-link',
  HamburgerMenuContainer: 'hamburger-menu-container',
  HamburgerOverlay: 'hamburger-overlay',
  ScrollBackwardButton: 'scroll-backward-button',
  ScrollForwardButton: 'scroll-forward-button',
} as const;
